import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { DocsLayout } from "@/features/help/DocsLayout";
import { useAuth } from "@/auth/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { listAccessibleCategories } from "@/features/help/docsContent";

/**
 * Docs overview (right column). Topic index lives in the left column of DocsLayout.
 */
export function DocsPage() {
  const { user } = useAuth();
  const { can } = usePermissions();
  const categories = listAccessibleCategories(user, can);
  const topicCount = categories.reduce(
    (count, category) => count + category.guides.length,
    0,
  );

  return (
    <DocsLayout
      title="Docs"
      description="Reference for modules and workflows in your workspace."
      actions={
        <Button asChild variant="outline">
          <Link to="/help">Get help</Link>
        </Button>
      }
    >
      <article className="space-y-4 text-sm leading-relaxed text-muted-foreground">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          Product documentation
        </h2>
        <p>
          This chapter documents the screens and workflows available in the
          home-care workspace: Care operations, directory, organization
          settings, your account, and—when you are a platform
          superuser—cross-tenant administration.
        </p>
        <p>
          Use the <span className="text-foreground">Topics</span> column to
          open a guide. {topicCount} topic
          {topicCount === 1 ? "" : "s"} are available for your role. For quick
          answers, see{" "}
          <Link
            to="/help"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            Get help
          </Link>
          .
        </p>
      </article>

      <div className="space-y-6">
        {categories.map((category) => (
          <section key={category.id} id={category.id} className="space-y-2">
            <div className="space-y-1">
              <h3 className="text-base font-semibold tracking-tight">
                {category.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {category.description}
              </p>
            </div>
            <ul className="grid gap-2 sm:grid-cols-2">
              {category.guides.map((guide) => (
                <li key={guide.slug}>
                  <Link
                    to={`/docs/${guide.slug}`}
                    className="block h-full rounded-md border px-3 py-2.5 transition-colors hover:bg-muted/50"
                  >
                    <span className="font-medium text-primary">
                      {guide.title}
                    </span>
                    <span className="mt-0.5 block text-sm text-muted-foreground">
                      {guide.summary}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </DocsLayout>
  );
}
